import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  X,
  Loader2,
  Trash2,
  Upload,
  FileText,
  Download,
  ExternalLink,
  Clock,
  AlertTriangle,
  CalendarDays,
  Banknote,
  MapPin,
  Factory,
  Link2,
  FolderKanban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateShort } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { MultiSelectTags } from "@/components/multi-select-tags";
import { DatePickerField } from "@/components/date-picker-field";
import {
  PROGRAM_LABELS,
  PROGRAM_INSTITUTIONS,
  SECTORS,
  SUPPORT_RATE_OPTIONS,
} from "@/lib/constants";
import { ConfirmModal } from "@/components/confirm-modal";

// ─── Types ──────────────────────────────────────

interface CallDocument {
  id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  download_url: string;
  created_at: string;
}

interface LinkedProject {
  id: number;
  title: string;
  client_name: string;
  status: string;
}

interface CallPeriod {
  id: number;
  program_type: string;
  title: string;
  call_number: string;
  institution: string;
  description: string;
  application_start: string;
  application_end: string;
  submission_deadline: string;
  support_rate: string;
  budget: string;
  eligible_cities: string;
  eligible_sectors: string;
  notes: string;
  source_url: string;
  status: string;
  project_count: number;
  documents: CallDocument[];
  projects?: LinkedProject[];
  created_at: string;
  updated_at: string;
}

interface CallPeriodResponse {
  data: CallPeriod;
}

interface Il {
  id: number;
  name: string;
  bolge: number;
}

// ─── Constants ──────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: {
    label: "Taslak",
    color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  },
  active: {
    label: "Aktif",
    color: "text-green-400 bg-green-500/10 border-green-500/20",
  },
  closed: {
    label: "Kapandı",
    color: "text-gray-400 bg-gray-500/10 border-gray-500/20",
  },
  upcoming: {
    label: "Yaklaşan",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  },
};

const STATUS_TRANSITIONS: Record<string, { label: string; next: string; color: string }> = {
  draft: {
    label: "Aktif Et",
    next: "active",
    color: "bg-green-600 text-white hover:bg-green-700",
  },
  active: {
    label: "Kapat",
    next: "closed",
    color: "bg-gray-600 text-white hover:bg-gray-700",
  },
  closed: {
    label: "Yeniden Aç",
    next: "active",
    color: "bg-blue-600 text-white hover:bg-blue-700",
  },
};

const SECTOR_OPTIONS = SECTORS.map((s, i) => ({ id: i + 1, name: s }));

// ─── Helpers ────────────────────────────────────

function getDaysUntilDeadline(deadline: string): number | null {
  if (!deadline) return null;
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffMs = deadlineDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseDateString(dateStr: string): Date | null {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function parseDateToInputValue(date: Date | null): string {
  if (!date) return "";
  try {
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
}

function splitToTags(value: string): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatBudgetInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("tr-TR");
}

function stringToTagOptions(
  value: string,
  allOptions: { id: number; name: string }[]
): { id: number; name: string }[] {
  const tags = splitToTags(value);
  return tags
    .map((tag) => {
      const found = allOptions.find(
        (o) => o.name.toLocaleLowerCase("tr") === tag.toLocaleLowerCase("tr")
      );
      return found || { id: Math.random(), name: tag };
    });
}

// ─── Main Component ─────────────────────────────

export function CallPeriodDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const { data, loading, error, refetch } = useFetch<CallPeriodResponse>(
    `/call-periods/${id}`
  );

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const cp = data.data;
  const statusInfo = STATUS_CONFIG[cp.status] || {
    label: cp.status,
    color: "text-muted-foreground",
  };

  const displayInstitution =
    PROGRAM_INSTITUTIONS[cp.program_type] || cp.institution || "";
  const deadlineDays = getDaysUntilDeadline(cp.submission_deadline);
  const cityTags = splitToTags(cp.eligible_cities);
  const sectorTags = splitToTags(cp.eligible_sectors);

  async function handleStatusChange(newStatus: string) {
    setChangingStatus(true);
    try {
      await adminFetch(`/call-periods/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      refetch();
    } catch {
      // Hata
    } finally {
      setChangingStatus(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await adminFetch(`/call-periods/${id}`, { method: "DELETE" });
      navigate("/call-periods");
    } catch {
      // Error handled
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate("/call-periods")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Çağrı Dönemlerine Dön
      </button>

      {/* Header Card */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-semibold text-foreground">
                {cp.title}
              </h1>
              <span
                className={cn(
                  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                  statusInfo.color
                )}
              >
                {statusInfo.label}
              </span>
              {STATUS_TRANSITIONS[cp.status] && (
                <button
                  onClick={() => handleStatusChange(STATUS_TRANSITIONS[cp.status].next)}
                  disabled={changingStatus}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-xs font-medium transition-colors",
                    STATUS_TRANSITIONS[cp.status].color,
                    changingStatus && "cursor-not-allowed opacity-50"
                  )}
                >
                  {changingStatus && <Loader2 className="h-3 w-3 animate-spin" />}
                  {STATUS_TRANSITIONS[cp.status].label}
                </button>
              )}
              {displayInstitution && (
                <span className="inline-flex items-center rounded-full border border-accent/30 bg-accent/10 px-2.5 py-0.5 text-xs font-medium text-accent">
                  {displayInstitution}
                </span>
              )}
              <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {PROGRAM_LABELS[cp.program_type] || cp.program_type}
              </span>
            </div>
            {cp.description && (
              <p className="mt-2 text-sm text-muted-foreground">
                {cp.description}
              </p>
            )}
            {cp.call_number && (
              <p className="mt-1 text-sm text-muted-foreground">
                Çağrı No: {cp.call_number}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
            >
              <Pencil className="h-3.5 w-3.5" />
              Düzenle
            </button>
            {(cp.project_count ?? 0) === 0 && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg border border-destructive/30 px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10",
                  deleting && "cursor-not-allowed opacity-50"
                )}
              >
                {deleting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                Sil
              </button>
            )}
          </div>
        </div>

        {/* Deadline countdown */}
        {cp.submission_deadline && deadlineDays !== null && (
          <div className="mt-4">
            {deadlineDays < 0 ? (
              <div className="inline-flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-400">
                <AlertTriangle className="h-4 w-4" />
                Süre doldu ({Math.abs(deadlineDays)} gün önce)
              </div>
            ) : deadlineDays === 0 ? (
              <div className="inline-flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-400">
                <Clock className="h-4 w-4" />
                Son gün bugün!
              </div>
            ) : (
              <div
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium",
                  deadlineDays <= 30
                    ? "border-amber-500/20 bg-amber-500/10 text-amber-400"
                    : "border-border bg-secondary text-muted-foreground"
                )}
              >
                <Clock className="h-4 w-4" />
                {deadlineDays} gün kaldı
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cp.application_start && (
          <InfoCard
            icon={CalendarDays}
            label="Başvuru Başlangıç"
            value={formatDateShort(cp.application_start)}
          />
        )}
        {cp.application_end && (
          <InfoCard
            icon={CalendarDays}
            label="Başvuru Bitiş"
            value={formatDateShort(cp.application_end)}
          />
        )}
        {cp.submission_deadline && (
          <InfoCard
            icon={CalendarDays}
            label="Son Teslim Tarihi"
            value={formatDateShort(cp.submission_deadline)}
          />
        )}
        {cp.support_rate && (
          <InfoCard
            icon={Banknote}
            label="Destek Oranı"
            value={cp.support_rate}
          />
        )}
        {cp.budget && (
          <InfoCard icon={Banknote} label="Bütçe" value={cp.budget} />
        )}
        {cp.source_url && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <Link2 className="h-3.5 w-3.5" />
              Kaynak URL
            </div>
            <a
              href={cp.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-sm text-accent transition-colors hover:text-accent/80"
            >
              Kaynağa Git
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>

      {/* Eligible Cities as tags */}
      {cityTags.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            Uygun İller
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {cityTags.map((city) => (
              <span
                key={city}
                className="inline-flex items-center rounded-md border border-accent/30 bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
              >
                {city}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Eligible Sectors as tags */}
      {sectorTags.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Factory className="h-3.5 w-3.5" />
            Uygun Sektörler
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {sectorTags.map((sector) => (
              <span
                key={sector}
                className="inline-flex items-center rounded-md border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-foreground"
              >
                {sector}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {cp.notes && (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-sm font-semibold text-foreground">Notlar</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
            {cp.notes}
          </p>
        </div>
      )}

      {/* Documents Section */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-sm font-semibold text-foreground">Belgeler</h2>
        <div className="mt-4">
          <DocumentsSection
            callPeriodId={id!}
            documents={cp.documents || []}
            refetchParent={refetch}
          />
        </div>
      </div>

      {/* Linked Projects */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-sm font-semibold text-foreground">
          Bağlı Projeler ({cp.project_count ?? 0})
        </h2>
        {(cp.projects?.length ?? 0) === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Bu çağrı dönemine bağlı proje bulunmuyor.
          </p>
        ) : (
          <div className="mt-4 divide-y divide-border rounded-lg border border-border">
            {cp.projects!.map((project) => (
              <div
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="flex cursor-pointer items-center justify-between p-3 transition-colors hover:bg-secondary/50"
              >
                <div className="flex items-center gap-3">
                  <FolderKanban className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {project.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {project.client_name}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {project.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditModal
          callPeriod={cp}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false);
            refetch();
          }}
        />
      )}

      {/* Delete Confirm Modal */}
      <ConfirmModal
        open={showDeleteConfirm}
        title="Çağrı Dönemini Sil"
        description="Bu çağrı dönemini silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmLabel="Sil"
        cancelLabel="İptal"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

// ─── Info Card ──────────────────────────────────

function InfoCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1.5 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

// ─── Documents Section ──────────────────────────

function DocumentsSection({
  callPeriodId,
  documents,
  refetchParent,
}: {
  callPeriodId: string;
  documents: CallDocument[];
  refetchParent: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const API_BASE = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/admin`
        : "/api/admin";

      const headers: Record<string, string> = {};
      if (import.meta.env.DEV) {
        headers["X-Admin-Email"] = "abdulahcicekli@gmail.com";
      }
      const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
      if (cfMatch) headers["Cf-Access-Jwt-Assertion"] = cfMatch[1];

      const res = await fetch(
        `${API_BASE}/call-periods/${callPeriodId}/documents/upload`,
        {
          method: "POST",
          headers,
          body: formData,
        }
      );

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json?.error?.message || "Yükleme başarısız");
      }

      refetchParent();
    } catch {
      // Error handled via UI
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteDocument(docId: number) {
    try {
      await adminFetch(`/call-documents/${docId}`, { method: "DELETE" });
      refetchParent();
    } catch {
      // Error handled
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  }

  return (
    <div className="space-y-4">
      {/* Upload area */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors",
          dragOver
            ? "border-accent bg-accent/5"
            : "border-border hover:border-accent/50 hover:bg-secondary/50"
        )}
      >
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        ) : (
          <Upload className="h-8 w-8 text-muted-foreground" />
        )}
        <p className="mt-3 text-sm text-muted-foreground">
          {uploading
            ? "Yükleniyor..."
            : "Dosya yüklemek için tıklayın veya sürükleyin"}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      {/* Document list */}
      {documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Henüz belge yüklenmemiş.
        </p>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {doc.file_name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>-</span>
                    <span>{formatDateShort(doc.created_at)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {doc.download_url && (
                  <a
                    href={doc.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                )}
                <button
                  onClick={() => handleDeleteDocument(doc.id)}
                  className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Edit Modal ─────────────────────────────────

function EditModal({
  callPeriod,
  onClose,
  onSaved,
}: {
  callPeriod: CallPeriod;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formProgramType, setFormProgramType] = useState(
    callPeriod.program_type || ""
  );
  const [formTitle, setFormTitle] = useState(callPeriod.title || "");
  const [formCallNumber, setFormCallNumber] = useState(
    callPeriod.call_number || ""
  );
  const [formDescription, setFormDescription] = useState(
    callPeriod.description || ""
  );
  const [formApplicationStart, setFormApplicationStart] = useState<Date | null>(
    parseDateString(callPeriod.application_start)
  );
  const [formApplicationEnd, setFormApplicationEnd] = useState<Date | null>(
    parseDateString(callPeriod.application_end)
  );
  const [formSubmissionDeadline, setFormSubmissionDeadline] =
    useState<Date | null>(parseDateString(callPeriod.submission_deadline));
  const [formSupportRate, setFormSupportRate] = useState(
    callPeriod.support_rate || ""
  );
  const [formBudget, setFormBudget] = useState(callPeriod.budget || "");
  const [formNotes, setFormNotes] = useState(callPeriod.notes || "");
  const [formSourceUrl, setFormSourceUrl] = useState(
    callPeriod.source_url || ""
  );
  const [submitting, setSubmitting] = useState(false);

  // City data
  const [iller, setIller] = useState<{ id: number; name: string }[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [formSelectedCities, setFormSelectedCities] = useState<
    { id: number; name: string }[]
  >([]);
  const [formSelectedSectors, setFormSelectedSectors] = useState<
    { id: number; name: string }[]
  >(stringToTagOptions(callPeriod.eligible_sectors, SECTOR_OPTIONS));

  const derivedInstitution = formProgramType
    ? PROGRAM_INSTITUTIONS[formProgramType] || ""
    : "";

  // Fetch cities
  useEffect(() => {
    setCitiesLoading(true);
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api`
      : "/api";
    fetch(`${API_BASE}/il`)
      .then((r) => r.json())
      .then((json) => {
        const items: Il[] = Array.isArray(json.data) ? json.data : [];
        const sorted = items
          .sort((a, b) => a.name.localeCompare(b.name, "tr"))
          .map((il) => ({ id: il.id, name: il.name }));
        setIller(sorted);
        // Initialize selected cities from existing data
        if (callPeriod.eligible_cities) {
          setFormSelectedCities(
            stringToTagOptions(callPeriod.eligible_cities, sorted)
          );
        }
      })
      .catch(() => {})
      .finally(() => setCitiesLoading(false));
  }, [callPeriod.eligible_cities]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formProgramType || !formTitle.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        program_type: formProgramType,
        title: formTitle,
        call_number: formCallNumber || undefined,
        institution: derivedInstitution || undefined,
        description: formDescription || undefined,
        application_start:
          parseDateToInputValue(formApplicationStart) || undefined,
        application_end:
          parseDateToInputValue(formApplicationEnd) || undefined,
        submission_deadline:
          parseDateToInputValue(formSubmissionDeadline) || undefined,
        support_rate: formSupportRate || undefined,
        budget: formBudget || undefined,
        eligible_cities:
          formSelectedCities.length > 0
            ? formSelectedCities.map((c) => c.name).join(", ")
            : undefined,
        eligible_sectors:
          formSelectedSectors.length > 0
            ? formSelectedSectors.map((s) => s.name).join(", ")
            : undefined,
        notes: formNotes || undefined,
        source_url: formSourceUrl || undefined,
      };
      await adminFetch(`/call-periods/${callPeriod.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      onSaved();
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Çağrı Dönemini Düzenle
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Program Type + Auto-derived Institution */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Program Türü *
              </label>
              <select
                required
                value={formProgramType}
                onChange={(e) => setFormProgramType(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">Seçin...</option>
                {Object.entries(PROGRAM_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Kurum
              </label>
              <div className="flex h-9 items-center rounded-lg border border-border bg-secondary/50 px-3 text-sm text-muted-foreground">
                {derivedInstitution || "Program türü seçildiğinde otomatik belirlenir"}
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Başlık *
            </label>
            <input
              type="text"
              required
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Call Number */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Çağrı Numarası
            </label>
            <input
              type="text"
              value={formCallNumber}
              onChange={(e) => setFormCallNumber(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Açıklama
            </label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Date Fields */}
          <div className="grid gap-4 sm:grid-cols-3">
            <DatePickerField
              label="Başvuru Başlangıç"
              value={formApplicationStart}
              onChange={setFormApplicationStart}
            />
            <DatePickerField
              label="Başvuru Bitiş"
              value={formApplicationEnd}
              onChange={setFormApplicationEnd}
            />
            <DatePickerField
              label="Son Teslim Tarihi"
              value={formSubmissionDeadline}
              onChange={setFormSubmissionDeadline}
            />
          </div>

          {/* Support Rate + Budget */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Destek Oranı
              </label>
              <select
                value={formSupportRate}
                onChange={(e) => setFormSupportRate(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">Seçin...</option>
                {SUPPORT_RATE_OPTIONS.map((rate) => (
                  <option key={rate} value={rate}>
                    {rate}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Bütçe (TL)
              </label>
              <input
                type="text"
                placeholder="ör. 50.000.000"
                value={formBudget}
                onChange={(e) =>
                  setFormBudget(formatBudgetInput(e.target.value))
                }
                className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>

          {/* Eligible Cities */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Uygun İller
            </label>
            <MultiSelectTags
              options={iller}
              selected={formSelectedCities}
              onChange={setFormSelectedCities}
              placeholder="İl ara..."
              loading={citiesLoading}
            />
          </div>

          {/* Eligible Sectors */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Uygun Sektörler
            </label>
            <MultiSelectTags
              options={SECTOR_OPTIONS}
              selected={formSelectedSectors}
              onChange={setFormSelectedSectors}
              placeholder="Sektör ara..."
            />
          </div>

          {/* Source URL */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Kaynak URL
            </label>
            <input
              type="text"
              value={formSourceUrl}
              onChange={(e) => setFormSourceUrl(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Notlar
            </label>
            <textarea
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                submitting && "cursor-not-allowed opacity-50"
              )}
            >
              {submitting && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
